import { basesDefaults, blockAsParsed } from '../teamsDefaults';
import { type Bundle } from './types';
import { num, isChance, type Err } from './common';

/**
 * The `bases` block (ADR 0030): a port of packages/shared-cs/Runtime/Content/ContentValidator.Bases.cs with the same
 * messages in the same order. An absent block or field is its C# default (`basesDefaults`); explicit `null` is missing.
 */
export function validateBases(b: Bundle, err: Err) {
  const r = blockAsParsed(basesDefaults, b.bases);
  if (!r) return;
  const fail = (field: string, message: string) => err('bases', undefined, field, message);
  const notNegative = (field: Exclude<keyof typeof r, 'caravansEnabled' | 'autoHomeEnabled'> & string) => {
    if (num(r[field]) < 0) fail(field, `Bases ${field} is negative.`);
  };
  const atLeast = (field: Exclude<keyof typeof r, 'caravansEnabled' | 'autoHomeEnabled'> & string, min: number) => {
    if (num(r[field]) < min) fail(field, `Bases ${field} must be at least ${min}.`);
  };
  notNegative('maxActiveCamps');
  notNegative('campMinDistanceFromHomeM');
  notNegative('campMinDistanceBetweenM');
  notNegative('campDropCooldownHours');
  notNegative('maxCampDropsPer30Days');
  notNegative('checkInRadiusM');
  notNegative('checkInMinGapHours');
  notNegative('homeMoveCooldownDays');
  notNegative('initialHomeGraceHours');
  if (!(num(r.dropMaxAccuracyM) > 0))
    fail('dropMaxAccuracyM', 'Bases dropMaxAccuracyM must be above 0.');
  notNegative('dropAfterRelocationMinutes');
  atLeast('promoteMinCheckIns', 1);
  atLeast('promoteMinDistinctDays', 1);
  notNegative('promoteMinSpanHours');
  atLeast('promoteMinSessions', 1);
  atLeast('campExpiryDays', 1);
  notNegative('campExpiryWarningHours');
  if (!isChance(num(r.campRegenMult))) fail('campRegenMult', 'Bases need campRegenMult in 0..1.');
  notNegative('caravanMinHours');
  notNegative('caravanHoursPer100Km');
  if (num(r.caravanMaxHours) < num(r.caravanMinHours))
    fail('caravanMaxHours', 'Bases need caravanMaxHours >= caravanMinHours.');
  atLeast('autoDropSettleFixes', 1);
  if (num(r.autoDropWindowFixes) < num(r.autoDropSettleFixes))
    fail('autoDropWindowFixes', 'Bases need autoDropWindowFixes >= autoDropSettleFixes.');
  notNegative('autoDropSettleSeconds');
  if (!(num(r.autoDropMaxAccuracyM) > 0))
    fail('autoDropMaxAccuracyM', 'Bases autoDropMaxAccuracyM must be above 0.');
  if (!(num(r.autoDropMaxFixAgeSeconds) > 0))
    fail('autoDropMaxFixAgeSeconds', 'Bases autoDropMaxFixAgeSeconds must be above 0.');
  if (!(num(r.autoDropBackoffSeconds) > 0))
    fail('autoDropBackoffSeconds', 'Bases autoDropBackoffSeconds must be above 0.');
  if (num(r.autoDropBackoffMaxSeconds) < num(r.autoDropBackoffSeconds))
    fail('autoDropBackoffMaxSeconds', 'Bases need autoDropBackoffMaxSeconds >= autoDropBackoffSeconds.');
  if (num(r.promoteMinDistinctDays) > num(r.promoteMinCheckIns))
    fail('promoteMinDistinctDays', 'Bases need promoteMinDistinctDays <= promoteMinCheckIns.');
}
